import { useMemo, useState } from "react";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import Formulario from "../../Componentes/Utils/Formulario";
import FormComp from "../../Componentes/FormComp";
import useApi from "../../hooks/useApi";
import useValidateColaborador from "../../hooks/useValidateColaborador";

export default function ModalAtribuirResponsavel({
  onClose,
  equipamentos,
  equipamentoId,
  defaultEquipamentoId,
  onAssigned,
}) {
  const api = useApi();

  const lockedEquipamento = useMemo(() => {
    const id = equipamentoId ?? defaultEquipamentoId ?? "";
    if (!id) return null;
    return (equipamentos ?? []).find((x) => x.id === id) ?? null;
  }, [defaultEquipamentoId, equipamentoId, equipamentos]);

  const equipamentoOptions = useMemo(() => {
    return (equipamentos ?? []).map((e) => `${e.id} | ${e.patrimonio} | ${e.descricao}`);
  }, [equipamentos]);

  const byLabel = useMemo(() => {
    const m = new Map();
    (equipamentos ?? []).forEach((e) => {
      m.set(`${e.id} | ${e.patrimonio} | ${e.descricao}`, e.id);
    });
    return m;
  }, [equipamentos]);

  const defaultLabel = useMemo(() => {
    if (lockedEquipamento) {
      return `${lockedEquipamento.id} | ${lockedEquipamento.patrimonio} | ${lockedEquipamento.descricao}`;
    }
    const e = (equipamentos ?? []).find((x) => x.id === defaultEquipamentoId);
    if (!e) return "";
    return `${e.id} | ${e.patrimonio} | ${e.descricao}`;
  }, [defaultEquipamentoId, equipamentos, lockedEquipamento]);

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    equipamentoLabel: defaultLabel,
    matricula: "",
    email: "",
  });

  const {
    info: responsavelInfo,
    validating,
    error: validationError,
  } = useValidateColaborador(form.matricula, { bloquearDemitido: true });

  const emailValido = useMemo(() => {
    const e = String(form.email || "").trim();
    if (!e) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  }, [form.email]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === "matricula" ? value.replace(/\D/g, "") : value;
    setForm((prev) => ({ ...prev, [name]: nextValue }));
  };

  const canSubmit = useMemo(() => {
    const matOk = String(form.matricula || "").trim().length > 0;
    if (!matOk) return false;
    if (!responsavelInfo || validating || validationError) return false;
    if (!emailValido) return false;
    if (lockedEquipamento) return true;
    return Boolean(form.equipamentoLabel) && Boolean(byLabel.get(form.equipamentoLabel));
  }, [
    byLabel,
    emailValido,
    form.equipamentoLabel,
    form.matricula,
    lockedEquipamento,
    responsavelInfo,
    validating,
    validationError,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setSaving(true);
      const resolvedId = lockedEquipamento
        ? lockedEquipamento.id
        : byLabel.get(form.equipamentoLabel);

      await api.post("/Painel/API/InventarioTI/SaveResponsavel/", {
        id_ativo: resolvedId,
        matricula: String(form.matricula || "").trim(),
        email: String(form.email || "").trim(),
      });
      onClose?.();
      onAssigned?.();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erro ao atribuir responsável.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay state={true}>
      <Modal header close={onClose} size="md">
        <Formulario onSubmit={handleSubmit}>
          {lockedEquipamento ? (
            <FormComp
              title="EQUIPAMENTO"
              type="text"
              readOnly
              valor={`${lockedEquipamento.id} | ${lockedEquipamento.patrimonio} | ${lockedEquipamento.descricao}`}
            />
          ) : (
            <FormComp
              title="EQUIPAMENTO"
              type="select"
              options={equipamentoOptions}
              valor={form.equipamentoLabel}
              name="equipamentoLabel"
              changeFunc={handleChange}
              required
            />
          )}
          <FormComp
            title="MATRÍCULA DO RESPONSÁVEL"
            type="numberOnly"
            placeholder="EX: 012345"
            valor={form.matricula}
            name="matricula"
            changeFunc={handleChange}
            required
            maxLength={6}
          />
          <FormComp
            title="NOME"
            type="text"
            readOnly
            valor={responsavelInfo?.nome || ""}
            placeholder={validating ? "Validando matrícula..." : ""}
          />
          <FormComp
            title="FUNÇÃO"
            type="text"
            readOnly
            valor={responsavelInfo?.funcao || ""}
          />
          <FormComp
            title="CENTRO DE CUSTO"
            type="text"
            readOnly
            valor={responsavelInfo?.centro_custo_desc || responsavelInfo?.centro_custo || ""}
          />
          <FormComp
            title="E-MAIL DO COLABORADOR"
            type="email"
            placeholder="EX: colaborador@palmont.com.br"
            valor={form.email}
            name="email"
            changeFunc={handleChange}
            required
            invalid={form.email.length > 0 && !emailValido}
          />
          {validationError && (
            <p style={{ color: "#B91C1C", fontSize: "12px", marginTop: "-6px" }}>
              {validationError}
            </p>
          )}
          <FormComp
            type="submit"
            valor={saving ? "SALVANDO..." : validating ? "VALIDANDO..." : "SALVAR"}
            disabled={saving || validating || !canSubmit}
          />
        </Formulario>
      </Modal>
    </Overlay>
  );
}
